import React, { useState } from 'react'
import { Modal, Radio, Checkbox, Spin } from 'antd'
import { CSVLink } from 'react-csv'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import Cancel from 'assets/Images/close.svg'
import { exportBlogsData } from 'api/Blogs/Blogs'
import { failureMessage } from 'api/global'

const allHeaders = [
  { label: 'Title', key: 'title' },
  { label: 'Excerpt', key: 'excerpt' },
  { label: 'Visibility', key: 'visibility' },
  { label: 'Reading Time (min)', key: 'readingTime' },
  { label: 'Organization', key: 'organization' },
  { label: 'Authors', key: 'authors' },
  { label: 'Created At', key: 'createdAt' },
]

const flattenBlog = (blog: any) => ({
  title: blog.title || '',
  excerpt: blog.excerpt || '',
  visibility: blog.visibility || '',
  readingTime: blog.readingTime ?? '',
  organization: blog.organization?.name || '',
  authors: (blog.authors || []).map((a: any) => `${a.firstName} ${a.lastName}`).join('; '),
  createdAt: blog.createdAt ? new Date(blog.createdAt).toLocaleDateString() : '',
})

const ExportBlog = ({ visible, onClose }: { visible: boolean; onClose: () => void }) => {
  const [fileFormat, setFileFormat] = useState('csv')
  const [selectedColumns, setSelectedColumns] = useState(['Title', 'Visibility', 'Organization', 'Authors'])
  const [csvData, setCsvData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [ready, setReady] = useState(false)

  const handleColumnChange = (checked: boolean, label: string) => {
    setSelectedColumns(prev => checked ? [...prev, label] : prev.filter(c => c !== label))
    setReady(false)
  }

  const filteredHeaders = allHeaders.filter(h => selectedColumns.includes(h.label))

  const fetchAndExport = async () => {
    setLoading(true)
    try {
      const blogs: any[] = await exportBlogsData()
      const flat = blogs.map(flattenBlog)

      const filtered = flat.map(row => {
        const r: Record<string, any> = {}
        filteredHeaders.forEach(h => { r[h.key] = row[h.key] })
        return r
      })

      if (fileFormat === 'csv') {
        setCsvData(filtered)
        setReady(true)
        // trigger click after state flushes
        setTimeout(() => document.getElementById('blog-csv-link')?.click(), 100)
      } else if (fileFormat === 'excel') {
        const ws = XLSX.utils.json_to_sheet(filtered)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Blogs')
        XLSX.writeFile(wb, 'Blogs.xlsx')
        onClose()
      } else if (fileFormat === 'pdf') {
        const doc = new jsPDF()
        ;(doc as any).autoTable({
          head: [filteredHeaders.map(h => h.label)],
          body: filtered.map(row => filteredHeaders.map(h => row[h.key])),
        })
        doc.save('Blogs.pdf')
        onClose()
      }
    } catch {
      failureMessage('Failed to export blogs')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal visible={visible} footer={false} onCancel={onClose}>
      <div className="popup-header">
        <h2>Export Blogs</h2>
        <img src={Cancel} alt="close" onClick={onClose} style={{ cursor: 'pointer' }} />
      </div>

      <div className="formatText">Format</div>
      <Radio.Group onChange={e => { setFileFormat(e.target.value); setReady(false) }} value={fileFormat}>
        <Radio value="csv">CSV</Radio>
        <Radio value="excel">Excel</Radio>
        <Radio value="pdf">PDF</Radio>
      </Radio.Group>

      <div className="radioText mt-15px">Select columns to export</div>
      <div className="checkbox-group">
        {allHeaders.map(({ label }) => (
          <div key={label}>
            <Checkbox checked={selectedColumns.includes(label)} onChange={e => handleColumnChange(e.target.checked, label)}>
              {label}
            </Checkbox>
          </div>
        ))}
      </div>

      <div className="flex justify-end w-full mt-10px">
        <button className="prim-sm-btn" style={{ background: '#EBEDF1', color: 'black', marginRight: '10px' }} onClick={onClose}>Cancel</button>
        <button className="prim-sm-btn" onClick={fetchAndExport} disabled={loading || selectedColumns.length === 0}>
          {loading ? <Spin size="small" /> : 'Export'}
        </button>
      </div>

      {ready && (
        <CSVLink
          data={csvData}
          headers={filteredHeaders}
          filename="Blogs.csv"
          id="blog-csv-link"
          style={{ display: 'none' }}
          onClick={onClose}
        />
      )}
    </Modal>
  )
}

export default ExportBlog
